import { Canvas3d } from 'cvat-canvas3d-wrapper';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { ActiveControl, CombinedState } from 'reducers';
import Button from 'antd/lib/button';
import { getCore } from 'cvat-core-wrapper';
import {
    switchPlay,
    updateTranscript, updateTranscriptActivateIdx,
} from 'actions/annotation-actions';
import { personColors } from './conf';
import {
    frameToRelativeSeconds,
    getFramesDurationSeconds,
    getTimelineFrameRate,
    secondsToFrame,
} from 'utils/media-time';

import {Property} from 'csstype';

const cvat = getCore();

interface StateToProps {
    canvasInstance: Canvas3d;
    activeControl: ActiveControl;
    labels: any[];
    jobInstance: any;
    startFrame: number,
    stopFrame: number,
    playing: boolean,
    frameNumber: number;
    states: any[];
    jobPhase: string;
    transcriptData: any;
    dataFrameRate: number | null;
    activatedIdx: number;
    segmentText: string;
}

interface DispatchToProps {
    onSwitchPlay(play: boolean): void;
    onChangeTranscript(index: number, segment: any): void;
    setActivatedIdx(index: number): void;
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        onSwitchPlay(play: boolean): void {
            dispatch(switchPlay(play));
        },
        onChangeTranscript(index: number, segment: any): void {
            dispatch(updateTranscript(index, segment));
        },
        setActivatedIdx(index: number): void {
            dispatch(updateTranscriptActivateIdx(index));
        }
    };
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            canvas: { instance: canvasInstance, activeControl },
            job: { labels, instance: jobInstance },
            player: {
                playing,
                transcript: {
                    data: transcriptData,
                    activatedIdx,
                    currentSegment: {
                        text: segmentText
                    }
                },
                frame: {
                    number: frameNumber,

                },
            },
            annotations: { states },

        },
    } = state;

    const {startFrame, stopFrame, phase: jobPhase} = jobInstance;

    return {
        canvasInstance: canvasInstance as Canvas3d,
        activeControl,
        labels,
        jobInstance,
        startFrame,
        stopFrame,
        playing,
        frameNumber,
        states,
        jobPhase,
        transcriptData,
        dataFrameRate: jobInstance.dataFrameRate,
        activatedIdx,
        segmentText,
    };
}

interface Props {
}

const MULTIPLIER_WIDTH = 2;
function WaveformSlider(props: StateToProps & DispatchToProps & Props): JSX.Element {


    const {
        startFrame, stopFrame, frameNumber,
        onSwitchPlay,
        playing,
        transcriptData,
        dataFrameRate,
        onChangeTranscript,
        jobInstance,
        activatedIdx,
        setActivatedIdx,
        segmentText
    } = props;
    const frameRate = getTimelineFrameRate(dataFrameRate);
    const relativeFrameNumber = frameNumber - startFrame;
    const maxTime = getFramesDurationSeconds(startFrame, stopFrame, frameRate);

    const sliderRef = useRef<HTMLDivElement>(null);

    const [leftBound, setLeftBound] = useState(-1);
    const [rightBound, setRightBound] = useState(-1);
    const [dragging, setDragging] = useState<String | null>(null);
    const [dragBeginLeftRightPos, setDragBeginLeftRightPos] = useState<[number, number, number] | null>(null);
    const frameToTrascriptSegmentMap = useRef<number[]>([]);


    useEffect(() => {

        const setDraggingNull = () => {
            setDragging(null);
            setDragBeginLeftRightPos(null);
        }
        const changeBound = (e: MouseEvent) => {
            if (sliderRef.current) {

                if (activatedIdx !== -1) {
                    const xPos = e.clientX;
                    const sliderMid = sliderRef.current.getBoundingClientRect().x + sliderRef.current.getBoundingClientRect().width / 2;
                    const newTime = frameToRelativeSeconds(
                        frameNumber + (xPos - sliderMid) / MULTIPLIER_WIDTH,
                        startFrame,
                        frameRate,
                    );
                    if (dragging === 'left') {
                        if (newTime < rightBound - 0.25) setLeftBound(Math.max(0, newTime));
                    }
                    else if (dragging === 'right') {
                        if (newTime > leftBound + 0.25) setRightBound(Math.min(maxTime, newTime));
                    }
                    else if (dragging === 'whole' && dragBeginLeftRightPos) {
                        const dragStartTime = frameToRelativeSeconds(
                            frameNumber + (dragBeginLeftRightPos[2] - sliderMid) / MULTIPLIER_WIDTH,
                            startFrame,
                            frameRate,
                        );
                        const leftDiff = dragStartTime - dragBeginLeftRightPos[0];
                        const rightDiff = dragBeginLeftRightPos[1] - dragStartTime;
                        setLeftBound(Math.min(Math.max(0, newTime - leftDiff), maxTime - (leftDiff + rightDiff)));
                        setRightBound(Math.min(Math.max(leftDiff + rightDiff, newTime + rightDiff), maxTime));
                    }
                }
            }
        }

        document.addEventListener('mouseup', setDraggingNull);
        document.addEventListener('mousemove', changeBound);

        return () => {
            document.removeEventListener('mouseup', setDraggingNull);
            document.removeEventListener('mousemove', changeBound);
        }
    }, [activatedIdx, dragging, sliderRef, frameNumber, frameNumber]);

    useEffect(() => {
        if (transcriptData?.segments?.length) {

            frameToTrascriptSegmentMap.current = new Array(stopFrame - startFrame + 1).fill(-1);
            transcriptData.segments.forEach((segment: any, index: number) => {

                const sgmtStartFrame = secondsToFrame(segment.start, startFrame, stopFrame, frameRate);
                const sgmtEndFrame = secondsToFrame(segment.end, startFrame, stopFrame, frameRate);
                for (let i = sgmtStartFrame; i<=sgmtEndFrame; i++) {
                    frameToTrascriptSegmentMap.current[i - startFrame] = index;
                }
            })
        } else {
            frameToTrascriptSegmentMap.current = [];
        }
    }, [frameRate, startFrame, stopFrame, transcriptData])

    useEffect(() => {
        if (activatedIdx !== -1) {
            onSwitchPlay(false);
            setLeftBound(transcriptData.segments[activatedIdx].start);
            setRightBound(transcriptData.segments[activatedIdx].end);
            // changeFrame(Math.min(Math.ceil(transcriptData.segments[activatedIdx].start * frameSpeed), stopFrame));
        }
    }, [activatedIdx, transcriptData]);


    const sliderWidth = sliderRef.current?.clientWidth || 0;
    const currentIdx = frameToTrascriptSegmentMap.current[relativeFrameNumber] != null ?
        frameToTrascriptSegmentMap.current[relativeFrameNumber] : -1;
    return (
        <>

            <div style={{width: '100%', margin: 'auto', height: '100%', position: 'relative', backgroundColor: '#eee', overflowX: 'clip'}}>
                <div style={{position: 'absolute', top: 0, left: '50%', width: '1px', height: '100%', backgroundColor: 'red'}}></div>
                <div style={{
                    backgroundImage: `url("/api/jobs/${jobInstance.id}/getWaveformImage")`,
                    backgroundColor: '#00000000',
                    backgroundSize: `${(stopFrame - startFrame)*MULTIPLIER_WIDTH}px 100%`,
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '100%',
                    backgroundPosition: sliderWidth ? `${(sliderWidth / 2 - MULTIPLIER_WIDTH * relativeFrameNumber)}px 0px` : '0px 0px',
                    cursor: (activatedIdx !== -1 || jobInstance.phase !== 'phase0' ) ? 'auto' : 'crosshair'

                }} onClick={(e) => {
                    if (sliderRef.current && activatedIdx === -1) {
                        onSwitchPlay(false);
                        const xPos = e.clientX;
                        const sliderMid = sliderRef.current.getBoundingClientRect().x + sliderRef.current.getBoundingClientRect().width / 2;
                        const newTime = frameToRelativeSeconds(
                            frameNumber + (xPos - sliderMid) / MULTIPLIER_WIDTH,
                            startFrame,
                            frameRate,
                        );

                        onChangeTranscript(-1, {
                            start: newTime,
                            end: newTime + 5,
                            text: '',
                            speaker: "SPEAKER_00"
                        })
                }}} ref={sliderRef}></div>


                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: -MULTIPLIER_WIDTH * relativeFrameNumber,
                    height: '100%'
                }}>
                    <WaveformHandlesMemo
                        startFrame={startFrame} stopFrame={stopFrame} activatedIdx={activatedIdx}
                        currentIdx={currentIdx} frameRate={frameRate} jobInstance={jobInstance} leftBound={leftBound}
                        onSwitchPlay={onSwitchPlay} playing={playing}
                        rightBound={rightBound} setActivatedIdx={setActivatedIdx}
                        setDragging={setDragging} sliderWidth={sliderWidth} transcriptData={transcriptData}
                        dragging={dragging}
                        setDragBeginLeftRightPos={setDragBeginLeftRightPos}
                    />
                </div>

                {activatedIdx !== -1 &&
                    <>
                        <Button type="primary" style={{
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            lineHeight: '20px',
                            zIndex: 1,
                            translate: '-150% 200%',
                        }} onClick={()=>{
                            if (playing) onSwitchPlay(false);
                            if (transcriptData?.segments[activatedIdx].text === '') {
                                onChangeTranscript(activatedIdx, null);
                            }
                            setActivatedIdx(-1);
                        }}>Cancel Changes</Button>
                        <Button
                            disabled = {segmentText.trim() === ''}
                            title={segmentText.trim() === '' ? 'Cannot apply when text is empty': 'Apply Changes'}
                            type="primary" style={{
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            lineHeight: '20px',
                            zIndex: 1,
                            translate: '0% 200%',
                        }} onClick={()=>{
                            if (playing) onSwitchPlay(false);
                            if (segmentText.trim() === '') {
                                // do not save if text is empty
                                return;
                            } else {
                                onChangeTranscript(activatedIdx, {
                                    ...transcriptData.segments[activatedIdx], start: leftBound, end: rightBound,
                                    text: segmentText
                                })
                            }
                            setActivatedIdx(-1);


                        }}>Apply Changes</Button>
                    </>
                }

            </div>
        </>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(WaveformSlider);

interface WaveformHandleProps {
    transcriptData: any;
    currentIdx: number;
    jobInstance: any;
    activatedIdx: number;
    startFrame: number;
    stopFrame: number;
    frameRate: number;
    sliderWidth: number;
    leftBound: number;
    rightBound: number;
    playing: boolean;

    onSwitchPlay: (play: boolean) => void;
    setActivatedIdx: (idx: number) => void;
    setDragging: (lOrR: string) => void;

    dragging: String | null;
    setDragBeginLeftRightPos: (x: [beginTime: number, endTime: number, clientX: number]) => void;

}
function WaveformHandlesComponent(props: WaveformHandleProps): JSX.Element {
    const {transcriptData, currentIdx, jobInstance, activatedIdx,
    frameRate,
    sliderWidth, leftBound, rightBound, playing,
    onSwitchPlay, setActivatedIdx, setDragging, dragging, setDragBeginLeftRightPos
    } = props;

    return (<>
    {
        transcriptData && transcriptData.segments.map((segment: any, idx: number) => {
            if (segment.speaker) {
                const color  = personColors[parseInt(segment.speaker.split('_')[1])];
                const currentlyBeingSpoken = idx === currentIdx;

                const sliderStyle = {
                    position: 'absolute' as Property.Position,
                    bottom: 0,
                    translate: '-50% 50%',
                    width: currentlyBeingSpoken ? '25px' : '20px',
                    height: currentlyBeingSpoken ? '25px' : '20px',
                    backgroundColor: color,
                    borderRadius: '25px',
                    boxShadow: '2px 2px 2px black',
                    // boxSizing: 'border-box' as Property.BoxSizing,
                    border: currentlyBeingSpoken ? '1px solid white' : 'none'

                };
                return (<>
                    {/* Left slider */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: sliderWidth/2 + MULTIPLIER_WIDTH * ((activatedIdx === idx ? leftBound : segment.start) * frameRate),
                            width: '1px', height: '100%',
                            backgroundColor: color,
                            opacity: activatedIdx === -1 || activatedIdx === idx ? 1 : 0.5,
                        }}>
                        <div style={{
                            ...sliderStyle,
                            display: activatedIdx === -1 || activatedIdx === idx ? 'block': 'none',
                            zIndex: 1,
                            cursor : dragging === 'left' ? 'grabbing' : 'grab',
                        }}  onMouseDown={() =>{
                            if (jobInstance.phase === 'phase0') {
                                if (activatedIdx === -1) {
                                    if (playing) onSwitchPlay(false);
                                    setActivatedIdx(idx);
                                }
                                if (!dragging) setDragging('left');
                            }
                        }}></div>

                    </div>


                    { activatedIdx === idx && <>
                        <div
                        style={{
                            position: 'absolute',
                            left: sliderWidth/2 + MULTIPLIER_WIDTH * ((activatedIdx === idx ? leftBound : segment.start) * frameRate),
                            width: MULTIPLIER_WIDTH * ((activatedIdx === idx ? (rightBound - leftBound) : (segment.end - segment.start)) * frameRate),
                            backgroundColor: color,
                            opacity: 0.7,
                            top: 0,
                            height: '100%',
                            cursor : dragging === 'whole' ? 'grabbing' : 'grab',
                            zIndex: 1
                        }}

                        onMouseDown={(e) =>{
                            setDragBeginLeftRightPos([
                                leftBound,
                                rightBound,
                                e.clientX
                            ])
                            if (!dragging) setDragging('whole');
                        }}
                        ></div>
                        {/* Right slider */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: sliderWidth/2 + MULTIPLIER_WIDTH * ((activatedIdx === idx ? rightBound : segment.end) * frameRate),
                                width: '1px',
                                height: '100%',
                                backgroundColor: color
                            }
                        }>
                            <div style={{
                                ...sliderStyle,
                                display: activatedIdx === -1 || activatedIdx === idx ? 'block': 'none',
                                zIndex: 1,
                                cursor : dragging === 'right' ? 'grabbing' : 'grab',
                            }} onMouseDown={() =>{
                                if (!dragging)  setDragging('right');
                            }}></div>
                        </div>
                    </>
                    }

                </>
                )
            }

            return <></>

        })
    }</>)
}

const WaveformHandlesMemo = React.memo(WaveformHandlesComponent);
